import { motion } from "framer-motion";
import { PieChart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OPTION_COLORS, AGE_SEGMENT_SHORT } from "./constants";
import { getTotalOptionsVoted, translateSegment, translateSex } from "./dataUtils";

export function SummaryTab({ results, options, optionColorMap, consulta }) {
  const { t } = useTranslation();
  const globalTotalOptions = getTotalOptionsVoted(results);

  return (
    <div className="space-y-6" data-testid="summary-tab">
      {/* Age Group Summary Cards */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <PieChart className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">{t('results.summaryByAge')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries({
            [t('results.youngGroup')]: { segments: ['menys de 20', 'de 20 a 29'], bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: '\uD83D\uDC76' },
            [t('results.adultGroup')]: { segments: ['de 30 a 39', 'de 40 a 49', 'de 50 a 59'], bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: '\uD83D\uDC68' },
            [t('results.seniorGroup')]: { segments: ['de 60 a 69', 'de 70 a 79', 'mes de 79'], bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: '\uD83D\uDC74' }
          }).map(([groupName, config]) => {
            const groupData = (results?.by_sex_age || []).filter(row => {
              const rowSegment = (row.age_segment || '').toLowerCase();
              return config.segments.some(seg => rowSegment.includes(seg.toLowerCase()));
            });

            let groupTotalVoters = 0, homeVotes = 0, donaVotes = 0;
            const optionTotals = {};
            groupData.forEach(row => {
              groupTotalVoters += row.total || 0;
              if (row.sex === 'Home') homeVotes += row.total || 0;
              if (row.sex === 'Dona') donaVotes += row.total || 0;
              Object.entries(row.options || {}).forEach(([opt, count]) => {
                optionTotals[opt] = (optionTotals[opt] || 0) + count;
              });
            });

            let topProposal = '-', topVotes = 0;
            Object.entries(optionTotals).forEach(([opt, count]) => {
              if (count > topVotes) { topVotes = count; topProposal = opt; }
            });

            // Detect tie
            const tiedProposals = Object.entries(optionTotals).filter(([, c]) => c === topVotes);
            const isTie = tiedProposals.length > 1;

            return (
              <div key={groupName} className={`${config.bg} ${config.border} border rounded-xl p-5`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{config.icon}</span>
                  <div>
                    <h3 className={`font-bold ${config.text}`}>{groupName}</h3>
                    <p className="text-xs text-slate-500">{config.segments.map(s => translateSegment(t, s.charAt(0).toUpperCase() + s.slice(1))).join(', ')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white/70 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{isTie ? t('results.tie') : t('results.winningProposal')}</p>
                    {isTie ? (
                      <div className="space-y-1">
                        {tiedProposals.map(([name]) => (
                          <div key={name} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: optionColorMap[name] || OPTION_COLORS[options.indexOf(name)] || '#6366f1' }} />
                            <span className="font-bold text-slate-900">{name}</span>
                            <span className="text-slate-500 text-sm ml-auto">{topVotes} {t('results.votes')}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: topProposal !== '-' ? (optionColorMap[topProposal] || OPTION_COLORS[options.indexOf(topProposal)] || '#6366f1') : '#94a3b8' }} />
                        <span className="font-bold text-slate-900">{topProposal}</span>
                        <span className="text-slate-500 text-sm ml-auto">{topVotes} {t('results.votes')}</span>
                      </div>
                    )}
                  </div>
                  <div className="bg-white/70 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t('results.totalVoters')}: {groupTotalVoters}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                        <span className="text-sm">{t('results.man')}: <strong>{homeVotes}</strong></span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-pink-500" />
                        <span className="text-sm">{t('results.woman')}: <strong>{donaVotes}</strong></span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t('results.breakdown')}</p>
                    <div className="space-y-1">
                      {[...options]
                        .sort((a, b) => (optionTotals[b] || 0) - (optionTotals[a] || 0))
                        .map((opt) => {
                        const count = optionTotals[opt] || 0;
                        const pct = globalTotalOptions > 0 ? (count / globalTotalOptions * 100).toFixed(1) : 0;
                        const originalIdx = options.indexOf(opt);
                        return (
                          <div key={opt} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded" style={{ backgroundColor: optionColorMap[opt] || OPTION_COLORS[originalIdx] }} />
                            <span className="text-xs text-slate-600 flex-1">{opt}</span>
                            <span className="text-xs font-mono font-semibold">{count}</span>
                            <span className="text-xs text-slate-400">({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Detailed Cross Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('results.fullDetail')}</h3>
        <div className="overflow-x-auto">
          <table className="data-table text-sm">
            <thead>
              <tr>
                <th>{t('results.segment')}</th>
                <th>{t('results.sexColumn')}</th>
                <th className="text-right">{t('results.votersColumn')}</th>
                {options.map((opt, idx) => (
                  <th key={opt} className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <div className="w-2 h-2 rounded" style={{ backgroundColor: optionColorMap[opt] || OPTION_COLORS[idx] }} />
                      {opt}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <DetailedRows results={results} options={options} optionColorMap={optionColorMap} globalTotalOptions={globalTotalOptions} t={t} />
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}

function DetailedRows({ results, options, globalTotalOptions, t }) {
  const byAge = {};
  (results?.by_sex_age || []).forEach(row => {
    const segment = row.age_segment || 'Desconegut';
    if (!byAge[segment]) byAge[segment] = { Home: null, Dona: null };
    if (row.sex === 'Home') byAge[segment].Home = row;
    if (row.sex === 'Dona') byAge[segment].Dona = row;
  });

  const segmentOrder = [
    'menors de 20 anys', 'de 20 a 29 anys', 'de 30 a 39 anys',
    'de 40 a 49 anys', 'de 50 a 59 anys', 'de 60 a 69 anys',
    'de 70 a 79 anys', 'majors de 79 anys'
  ];
  const sortedSegments = Object.keys(byAge).sort((a, b) => {
    const aIdx = segmentOrder.findIndex(s => a.toLowerCase().includes(s));
    const bIdx = segmentOrder.findIndex(s => b.toLowerCase().includes(s));
    return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
  });

  const rows = [];
  sortedSegments.forEach((segment) => {
    const data = byAge[segment];
    const homeRow = data.Home;
    const donaRow = data.Dona;
    const segmentTotalVoters = (homeRow?.total || 0) + (donaRow?.total || 0);
    const totalOptions = {};
    options.forEach(opt => { totalOptions[opt] = (homeRow?.options?.[opt] || 0) + (donaRow?.options?.[opt] || 0); });

    let topOptTotal = '-', topCountTotal = 0;
    Object.entries(totalOptions).forEach(([opt, count]) => { if (count > topCountTotal) { topCountTotal = count; topOptTotal = opt; } });

    const rowCount = (homeRow ? 1 : 0) + (donaRow ? 1 : 0) + 1;
    let isFirst = true;

    const renderOptionCells = (dataRow, topOpt) =>
      options.map((opt) => {
        const count = dataRow?.options?.[opt] || 0;
        const pct = globalTotalOptions > 0 ? (count / globalTotalOptions * 100).toFixed(1) : 0;
        return (
          <td key={opt} className={`text-right font-mono ${opt === topOpt ? 'font-bold' : ''}`}>
            {count}<span className="text-slate-400 text-xs ml-1">({pct}%)</span>
          </td>
        );
      });

    const findTop = (dataRow) => {
      let top = '-', best = 0;
      Object.entries(dataRow?.options || {}).forEach(([opt, count]) => { if (count > best) { best = count; top = opt; } });
      return top;
    };

    if (homeRow) {
      rows.push(
        <tr key={`${segment}-home`} className="bg-blue-50/30">
          {isFirst && <td rowSpan={rowCount} className="font-semibold align-middle border-b-2 border-slate-300">{translateSegment(t, AGE_SEGMENT_SHORT[segment] || segment)}</td>}
          <td><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-blue-500" />{t('results.man')}</div></td>
          <td className="text-right font-mono">{homeRow.total}</td>
          {renderOptionCells(homeRow, findTop(homeRow))}
        </tr>
      );
      isFirst = false;
    }

    if (donaRow) {
      rows.push(
        <tr key={`${segment}-dona`} className="bg-pink-50/30">
          {isFirst && <td rowSpan={rowCount} className="font-semibold align-middle border-b-2 border-slate-300">{translateSegment(t, AGE_SEGMENT_SHORT[segment] || segment)}</td>}
          <td><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-pink-500" />{t('results.woman')}</div></td>
          <td className="text-right font-mono">{donaRow.total}</td>
          {renderOptionCells(donaRow, findTop(donaRow))}
        </tr>
      );
      isFirst = false;
    }

    rows.push(
      <tr key={`${segment}-total`} className="bg-slate-100 border-b-2 border-slate-300">
        {isFirst && <td rowSpan={rowCount} className="font-semibold align-middle border-b-2 border-slate-300">{translateSegment(t, AGE_SEGMENT_SHORT[segment] || segment)}</td>}
        <td><span className="font-semibold text-slate-700">{t('results.total')}</span></td>
        <td className="text-right font-mono font-bold">{segmentTotalVoters}</td>
        {options.map((opt) => {
          const count = totalOptions[opt] || 0;
          const pct = globalTotalOptions > 0 ? (count / globalTotalOptions * 100).toFixed(1) : 0;
          return (
            <td key={opt} className={`text-right font-mono font-semibold ${opt === topOptTotal ? 'text-blue-600' : ''}`}>
              {count}<span className="text-slate-400 text-xs ml-1">({pct}%)</span>
            </td>
          );
        })}
      </tr>
    );
  });

  return <>{rows}</>;
}
